import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CheckCircle2, Mail, MessageCircle, Phone, Send, Store, X } from "lucide-react";
import { EASE } from "@/components/Reveal";
import { toast } from "@/components/ui/sonner";
import {
  ADDRESS,
  CAROUSELL_URL,
  EMAIL,
  PHONE_DISPLAY,
  PHONE_TEL,
  WHATSAPP_URL,
} from "@/components/Navbar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INPUT_CLS =
  "mt-1.5 w-full border-b border-charcoal/20 bg-transparent py-2.5 text-sm text-charcoal outline-none transition-colors duration-300 placeholder:text-charcoal/30 focus:border-jade";

const INITIAL = { name: "", phone: "", email: "", message: "" };

const QUICK_LINKS = [
  { id: "whatsapp", icon: MessageCircle, label: "WhatsApp", href: WHATSAPP_URL, external: true },
  { id: "call", icon: Phone, label: "Call", href: PHONE_TEL },
  { id: "email", icon: Mail, label: "Email", href: `mailto:${EMAIL}` },
  { id: "carousell", icon: Store, label: "Carousell", href: CAROUSELL_URL, external: true },
];

const EnquiryModal = ({ open, onClose }) => {
  const [form, setForm] = useState(INITIAL);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    const lenis = window.__lenis;
    if (lenis) {
      if (open) lenis.stop();
      else lenis.start();
    }
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const set = (key) => (e) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    setErrors((er) => ({ ...er, [key]: undefined }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const next = {};
    if (form.name.trim().length < 2) next.name = "Please enter your name.";
    if (!/^[+()\-.\s\d]{7,}$/.test(form.phone.trim()))
      next.phone = "Please enter a valid contact number.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
      next.email = "Please enter a valid email address.";
    if (form.message.trim().length < 10)
      next.message = "Please tell us a little more (min. 10 characters).";
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    setSending(true);
    try {
      const res = await fetch(`${API}/enquiry`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name.trim(),
          phone: form.phone.trim(),
          email: form.email.trim(),
          message: form.message.trim(),
        }),
      });
      if (!res.ok) throw new Error("send failed");
      setSent(true);
    } catch {
      toast.error("Could not send your enquiry", {
        description:
          "Please try again, or reach us directly on WhatsApp or email below.",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={onClose}
            className="absolute inset-0 bg-pine/60 backdrop-blur-sm"
            aria-hidden="true"
          />
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label="Send an enquiry"
            data-testid="enquiry-modal"
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ duration: 0.4, ease: EASE }}
            className="relative max-h-[90vh] w-full max-w-md overflow-y-auto rounded-xl bg-white p-6 shadow-2xl md:p-8"
          >
            <button
              type="button"
              onClick={onClose}
              data-testid="modal-close-button"
              aria-label="Close"
              className="absolute right-4 top-4 z-10 flex h-9 w-9 items-center justify-center rounded-full border border-charcoal/10 text-charcoal/60 transition-colors duration-300 hover:text-charcoal"
            >
              <X className="h-4 w-4" />
            </button>

            {sent ? (
              <div data-testid="modal-success-confirmation" className="py-6 text-center">
                <CheckCircle2
                  className="mx-auto h-11 w-11 text-jade"
                  strokeWidth={1.5}
                />
                <h3 className="mt-4 font-body text-xl font-bold text-forest">
                  Thank you{form.name ? `, ${form.name.trim().split(" ")[0]}` : ""}.
                </h3>
                <p className="mx-auto mt-2 max-w-xs text-sm leading-relaxed text-charcoal/60">
                  Your enquiry has been sent to our team. For anything urgent,
                  WhatsApp us at {PHONE_DISPLAY}.
                </p>
                <div className="mt-6 flex items-center justify-center gap-3">
                  <a
                    href={WHATSAPP_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="modal-success-whatsapp"
                    className="inline-flex items-center gap-2 rounded-sm bg-forest px-5 py-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-ivory transition-colors duration-300 hover:bg-jade"
                  >
                    <MessageCircle className="h-3.5 w-3.5" />
                    WhatsApp Us
                  </a>
                  <button
                    type="button"
                    data-testid="modal-reset-button"
                    onClick={() => {
                      setSent(false);
                      setForm(INITIAL);
                    }}
                    className="text-[11px] font-semibold uppercase tracking-[0.2em] text-jade transition-colors duration-300 hover:text-forest"
                  >
                    Send Another
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} noValidate data-testid="modal-form" className="space-y-5">
                <h3 className="pr-10 font-body text-lg font-bold text-forest">
                  Let&apos;s Talk
                </h3>

                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                  {QUICK_LINKS.map((q) => (
                    <a
                      key={q.id}
                      href={q.href}
                      {...(q.external
                        ? { target: "_blank", rel: "noopener noreferrer" }
                        : {})}
                      data-testid={`modal-${q.id}-button`}
                      className="flex items-center justify-center gap-1.5 rounded-sm border border-charcoal/15 py-2.5 text-[10px] font-semibold uppercase tracking-[0.15em] text-charcoal/70 transition-colors duration-300 hover:border-forest/40 hover:text-forest"
                    >
                      <q.icon className="h-3.5 w-3.5" />
                      {q.label}
                    </a>
                  ))}
                </div>

                <div className="flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-charcoal/40">
                  <span className="h-px flex-1 bg-charcoal/10" />
                  Or send an enquiry
                  <span className="h-px flex-1 bg-charcoal/10" />
                </div>

                <label className="block">
                  <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-charcoal/50">
                    Name
                  </span>
                  <input
                    type="text"
                    value={form.name}
                    onChange={set("name")}
                    placeholder="Your full name"
                    data-testid="modal-name-input"
                    className={INPUT_CLS}
                  />
                  {errors.name && (
                    <span role="alert" className="mt-1 block text-xs text-red-600">
                      {errors.name}
                    </span>
                  )}
                </label>

                <div className="grid gap-5 sm:grid-cols-2">
                  <label className="block">
                    <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-charcoal/50">
                      Contact Number
                    </span>
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={set("phone")}
                      placeholder="+65 9123 4567"
                      data-testid="modal-phone-input"
                      className={INPUT_CLS}
                    />
                    {errors.phone && (
                      <span role="alert" className="mt-1 block text-xs text-red-600">
                        {errors.phone}
                      </span>
                    )}
                  </label>
                  <label className="block">
                    <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-charcoal/50">
                      Email
                    </span>
                    <input
                      type="email"
                      value={form.email}
                      onChange={set("email")}
                      placeholder="you@company.com"
                      data-testid="modal-email-input"
                      className={INPUT_CLS}
                    />
                    {errors.email && (
                      <span role="alert" className="mt-1 block text-xs text-red-600">
                        {errors.email}
                      </span>
                    )}
                  </label>
                </div>

                <label className="block">
                  <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-charcoal/50">
                    Message
                  </span>
                  <textarea
                    rows={3}
                    value={form.message}
                    onChange={set("message")}
                    placeholder="How can we help your business?"
                    data-testid="modal-message-input"
                    className={`${INPUT_CLS} resize-none`}
                  />
                  {errors.message && (
                    <span role="alert" className="mt-1 block text-xs text-red-600">
                      {errors.message}
                    </span>
                  )}
                </label>

                <button
                  type="submit"
                  disabled={sending}
                  data-testid="modal-submit-button"
                  className="group flex w-full items-center justify-center gap-2.5 rounded-sm bg-forest py-4 text-[11px] font-semibold uppercase tracking-[0.2em] text-ivory transition-colors duration-300 hover:bg-jade disabled:opacity-60"
                >
                  {sending ? "Sending…" : "Send Enquiry"}
                  {!sending && (
                    <Send className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5" />
                  )}
                </button>
                <p className="text-center text-[10px] leading-relaxed text-charcoal/40">
                  {ADDRESS} · {EMAIL}
                </p>
              </form>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default EnquiryModal;
